import { HTMLAttributes } from "react";
import styled from "styled-components";
import { typographies } from "../../../styles/figmaTypographies";
import { Spacer } from "../Spacer";
import { TextCapitalized } from "../text/TextCapitalized";
import { colors } from "../../../styles/figmaColors";

export interface RowElementProps extends HTMLAttributes<HTMLButtonElement> {
  size: sizeRowElement;
  tooltip?: JSX.Element;
  onClickRow?: () => void;
  leftIcon?: JSX.Element;
  text: string | JSX.Element;
  subText?: string | JSX.Element;
  rightElement?: JSX.Element;
  rightElementText?: JSX.Element;
  bottomElement?: JSX.Element;
  borderRadius?: number;
  withoutShadow?: boolean;
  withoutBorder?: boolean;
  withoutSpacerBottom?: boolean;
  tagOrBadge?: JSX.Element;
  backgroundColor?: string;
  widthPercentageRightElementText?: number;
}

type sizeRowElement = "S" | "M" | "L" | "XL";

const rowOptions = {
  S: {
    spacer: 0.75,
    sizeIcon: 1,
    radius: 0.5,
    heightRow: 3,
    spacerBottom: 0.5,
  },
  M: {
    spacer: 1,
    sizeIcon: 1,
    radius: 1.125,
    heightRow: 3.5,
    spacerBottom: 0.75,
  },
  L: {
    spacer: 1.5,
    sizeIcon: 1.5,
    radius: 1.125,
    heightRow: 4.5,
    spacerBottom: 1,
  },
  XL: {
    spacer: 2,
    sizeIcon: 2,
    radius: 1.125,
    heightRow: 5.5,
    spacerBottom: 1.5,
  },
};

export const RowElement = (props: RowElementProps) => {
  const {
    text,
    leftIcon,
    onClickRow,
    size,
    rightElement,
    tooltip,
    subText,
    bottomElement,
    borderRadius,
    withoutShadow,
    withoutBorder,
    rightElementText,
    tagOrBadge,
    backgroundColor,
    widthPercentageRightElementText,
  } = props;
  const options = rowOptions[size];

  return (
    <StyledRowElement
      $radius={borderRadius ?? options.radius}
      $heightRow={options.heightRow + (bottomElement ? 2 : 0)}
      onClick={onClickRow}
      $hasClickableRow={!!onClickRow}
      $withoutShadow={withoutShadow ?? false}
      $withoutBorder={withoutBorder ?? false}
      $backgroundColor={
        backgroundColor ?? colors["colors/button/secondary/default"]
      }
    >
      <Spacer y={options.spacer} />
      <StyledColumn>
        <StyledFlex>
          <Spacer x={options.spacer} />
          {leftIcon && (
            <>
              <StyledLeftIcon $sizeIcon={options.sizeIcon}>
                {leftIcon}
              </StyledLeftIcon>
              {size === "S" ? (
                <Spacer x={0.25} />
              ) : size === "XL" ? (
                <Spacer x={1.5} />
              ) : (
                <Spacer x={1} />
              )}
            </>
          )}
          <StyledText>
            <StyledAllText>
              <StyledTitleAndTag>
                <TextCapitalized>{text}</TextCapitalized>
                {tagOrBadge && (
                  <StyledTagOrBagde>
                    <Spacer x={0.5} />
                    {tagOrBadge}
                  </StyledTagOrBagde>
                )}
              </StyledTitleAndTag>
              {subText && (
                <StyledSubText>
                  {size === "L" && <Spacer y={0.5} />}
                  {size === "XL" && <Spacer y={0.25} />}
                  <TextCapitalized>{subText}</TextCapitalized>
                </StyledSubText>
              )}
            </StyledAllText>
            {tooltip && (
              <StyledToolTip>
                <Spacer x={0.5} /> {tooltip}
              </StyledToolTip>
            )}
          </StyledText>
          {rightElementText && (
            <>
              <Spacer x={options.spacer} />
              <StyledRightElementText
                $widthPercentageRightElementText={
                  widthPercentageRightElementText ?? 100
                }
              >
                {rightElementText}
              </StyledRightElementText>
            </>
          )}
          {rightElement && (
            <>
              <Spacer x={options.spacer} />
              <div>{rightElement}</div>
            </>
          )}
          <Spacer x={options.spacer} />
        </StyledFlex>
        {bottomElement && (
          <>
            <Spacer y={options.spacerBottom} />
            <StyledFlex>
              <Spacer x={options.spacer} />
              {bottomElement}
              <Spacer x={options.spacer} />
            </StyledFlex>
          </>
        )}
      </StyledColumn>
      <Spacer y={options.spacer} />
    </StyledRowElement>
  );
};

interface StyledColumnArgs {
  $radius: number;
  $heightRow: number;
  $hasClickableRow: boolean;
  $withoutShadow: boolean;
  $withoutBorder: boolean;
  $backgroundColor: string;
}

interface StyledIconArgs {
  $sizeIcon: number;
}

const StyledTagOrBagde = styled.div`
  display: flex;
  align-items: center;
`;

const StyledTitleAndTag = styled.div`
  display: flex;
  align-items: center;
`;

const StyledRightElementText = styled.div<{
  $widthPercentageRightElementText: number;
}>`
  display: flex;
  justify-content: flex-end;
  width: ${({ $widthPercentageRightElementText }) =>
    $widthPercentageRightElementText}%;
`;

const StyledToolTip = styled.div`
  display: flex;
  & svg {
    width: 1.2rem;
    height: 1.2rem;
  }
`;

const StyledText = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
`;
const StyledAllText = styled.div`
  width: fit-content;
  display: flex;
  flex-direction: column;
`;

const StyledSubText = styled.div`
  ${typographies["Body/XXS"]}
`;

const StyledRowElement = styled.div<StyledColumnArgs>`
  ${typographies["Body/M"]}
  border-radius: ${({ $radius }) => $radius}rem;
  border: ${({ $withoutBorder }) =>
    $withoutBorder
      ? "none"
      : `0.0625rem solid ${colors["colors/borders/button/primary"]}`};
  width: 100%;
  background: ${({ $backgroundColor }) => $backgroundColor};
  color: ${colors["colors/text/black"]};
  height: ${({ $heightRow }) => $heightRow}rem;
  display: flex;
  flex-direction: column;
  cursor: ${({ $hasClickableRow }) =>
    $hasClickableRow ? "pointer" : "default"};
`;

const StyledLeftIcon = styled.div<StyledIconArgs>`
  ${({ $sizeIcon }) =>
    `width: ${$sizeIcon}rem; height:${$sizeIcon}rem;
  & svg {
    width: ${$sizeIcon}rem;
    height: ${$sizeIcon}rem;
  }`}
`;

const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
  height: fit-content;
`;

const StyledFlex = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 1.5rem;
`;
